import * as XLSX from 'xlsx';

// Standard impact categories for LCA analysis
const IMPACT_CATEGORIES = [
  { id: 'gwp', name: 'Global Warming Potential', unit: 'kg CO2-eq', shortName: 'GWP' },
  { id: 'ap', name: 'Acidification Potential', unit: 'kg SO2-eq', shortName: 'AP' },
  { id: 'ep', name: 'Eutrophication Potential', unit: 'kg PO4-eq', shortName: 'EP' },
  { id: 'odp', name: 'Ozone Depletion Potential', unit: 'kg CFC-11-eq', shortName: 'ODP' }
];

// Impact category mappings for flexible column detection
const impactMappings = {
  gwp: ['gwp', 'global_warming_potential', 'global warming potential', 'co2', 'co2-eq', 'kg co2-eq', 'carbon', 'carbon footprint'],
  ap: ['ap', 'acidification_potential', 'acidification potential', 'so2', 'so2-eq', 'kg so2-eq'],
  ep: ['ep', 'eutrophication_potential', 'eutrophication potential', 'po4', 'po4-eq', 'kg po4-eq'],
  odp: ['odp', 'ozone_depletion_potential', 'ozone depletion potential', 'cfc', 'cfc-11', 'cfc-11-eq', 'kg cfc-11-eq']
};

// Column mapping for flexible Excel parsing
const COLUMN_MAPPINGS = {
  component_name: ['component_name', 'component name', 'component', 'name', 'material', 'element', 'building component', 'building_component', 'buildingcomponent'],
  category: ['category', 'component_category', 'component category', 'type', 'group'],
  gwp: ['gwp', 'global_warming_potential', 'global warming potential', 'co2', 'co2-eq', 'kg co2-eq'],
  ap: ['ap', 'acidification_potential', 'acidification potential', 'so2', 'so2-eq', 'kg so2-eq'],
  ep: ['ep', 'eutrophication_potential', 'eutrophication potential', 'po4', 'po4-eq', 'kg po4-eq'],
  odp: ['odp', 'ozone_depletion_potential', 'ozone depletion potential', 'cfc', 'cfc-11-eq', 'kg cfc-11-eq']
};

// Building info column mappings
const BUILDING_INFO_MAPPINGS = {
  name: ['building_name', 'building name', 'project_name', 'project name', 'name', 'title'],
  area: ['area', 'building_area', 'building area', 'floor_area', 'floor area', 'm2', 'sqm', 'square_meters'],
  location: ['location', 'building_location', 'building location', 'city', 'country', 'address'],
  serviceLife: ['service_life', 'service life', 'lifespan', 'building_life', 'building life', 'years', 'lifetime'],
  energyUseKwhPerM2Yr: [
    'energy_use_kwh_per_m2_yr',
    'energy_use_kwh_m2_yr',
    'energy intensity',
    'energy_intensity',
    'energy intensity (kwh/m2/yr)',
    'energy impact',
    'energy_impact',
    'operational energy',
    'operational_energy',
    'b6 energy',
    'kwh/m2/yr',
    'energy use',
    'kwh/m2/year'
    ]
};

export class ExcelParser {
  constructor() {
    this.workbook = null;
    this.worksheet = null;
  }

  async parseFile(file) {
    try {
      // Read the Excel file
      const arrayBuffer = await file.arrayBuffer();
      this.workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get the first worksheet (components data)
      const firstSheetName = this.workbook.SheetNames[0];
      this.worksheet = this.workbook.Sheets[firstSheetName];
      
      // Parse the first worksheet (components data)
      const data = this.parseWorksheet();
      
      // Parse the second worksheet if it exists (LCA stages data)
      let lcaStagesData = {};
      if (this.workbook.SheetNames.length > 1) {
        const secondSheetName = this.workbook.SheetNames[1];
        const secondWorksheet = this.workbook.Sheets[secondSheetName];
        console.log('Found second worksheet:', secondSheetName);
        console.log('Available worksheets:', this.workbook.SheetNames);
        lcaStagesData = this.parseLCAStagesWorksheet(secondWorksheet);
        console.log('Final parsed LCA stages data:', lcaStagesData);
      } else {
        console.log('No second worksheet found. Available sheets:', this.workbook.SheetNames);
      }
      
      // Transform to LCA format
      const lcaData = this.transformToLCAData(data, lcaStagesData);
      
      return lcaData;
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      throw new Error(`Failed to parse Excel file: ${error.message}`);
    }
  }

  parseWorksheet() {
    // Convert worksheet to JSON
    const jsonData = XLSX.utils.sheet_to_json(this.worksheet, { header: 1 });
    
    if (jsonData.length < 2) {
      throw new Error('Excel file must have at least a header row and one data row');
    }

    const headers = jsonData[0];
    const rows = jsonData.slice(1);

    // Map column indices
    const columnMap = this.mapColumns(headers);
    
    // Parse building info from first few rows if available
    const buildingInfo = this.extractBuildingInfo(jsonData, columnMap);
    
    // Parse component data
    const components = this.parseComponents(rows, columnMap);
    
    // Store components for later use in LCA stages matching
    this.parsedComponents = components;
    
    return {
      buildingInfo,
      components,
      headers
    };
  }

  mapColumns(headers) {
    const columnMap = {};
    
    headers.forEach((header, index) => {
      if (!header) return;
      
      const headerStr = header.toString().toLowerCase().trim();
      
      // Map component columns
      for (const [key, possibleNames] of Object.entries(COLUMN_MAPPINGS)) {
        if (possibleNames.some(name => headerStr.includes(name))) {
          columnMap[key] = index;
          break;
        }
      }
      
      // Map building info columns
      for (const [key, possibleNames] of Object.entries(BUILDING_INFO_MAPPINGS)) {
        if (possibleNames.some(name => headerStr.includes(name))) {
          columnMap[`building_${key}`] = index;
          break;
        }
      }
    });

    // Ensure required columns exist
    if (columnMap.component_name === undefined) {
      throw new Error('Excel file must contain a column for component names (e.g., "Component Name", "Component", "Name", "Building component", "Building_component")');
    }

    return columnMap;
  }

  extractBuildingInfo(jsonData, columnMap) {
    const buildingInfo = {
      name: 'LCA Project',
      area: 1000, // default values
      location: 'Unknown',
      serviceLife: 50, // default service life in years
      energyUseKwhPerM2Yr: '' // optional energy intensity
    };

    try {
    // Try to find building info in the first few rows
    for (let i = 0; i < Math.min(5, jsonData.length); i++) {
      const row = jsonData[i];
      
      if (columnMap.building_name !== undefined && row[columnMap.building_name]) {
        buildingInfo.name = row[columnMap.building_name].toString();
      }
      if (columnMap.building_area !== undefined && row[columnMap.building_area]) {
        const area = parseFloat(row[columnMap.building_area]);
        if (!isNaN(area)) buildingInfo.area = area;
      }

      if (columnMap.building_location !== undefined && row[columnMap.building_location]) {
        buildingInfo.location = row[columnMap.building_location].toString();
      }
      if (columnMap.building_serviceLife !== undefined && row[columnMap.building_serviceLife]) {
        const serviceLife = parseInt(row[columnMap.building_serviceLife]);
        if (!isNaN(serviceLife)) buildingInfo.serviceLife = serviceLife;
      }
      if (columnMap.building_energyUseKwhPerM2Yr !== undefined && row[columnMap.building_energyUseKwhPerM2Yr] != null) {
        const energyUse = parseFloat(row[columnMap.building_energyUseKwhPerM2Yr]);
        if (!isNaN(energyUse)) buildingInfo.energyUseKwhPerM2Yr = energyUse;
      }
    }
    } catch (error) {
      console.warn('Error extracting building info, using defaults:', error);
    }

    // Ensure all required properties exist with valid values
    if (!buildingInfo.name || buildingInfo.name === '') buildingInfo.name = 'LCA Project';
    if (!buildingInfo.area || isNaN(buildingInfo.area)) buildingInfo.area = 1000;
    if (!buildingInfo.location || buildingInfo.location === '') buildingInfo.location = 'Unknown';
    if (!buildingInfo.serviceLife || isNaN(buildingInfo.serviceLife)) buildingInfo.serviceLife = 50;

    console.log('Extracted building info:', buildingInfo);
    return buildingInfo;
  }

  parseComponents(rows, columnMap) {
    const components = [];
    
    rows.forEach((row, index) => {
      // Skip empty rows
      if (!row[columnMap.component_name] || row[columnMap.component_name].toString().trim() === '') {
        return;
      }

      const component = {
        id: `component_${index}`,
        name: row[columnMap.component_name].toString().trim(),
        category: this.determineCategory(row, columnMap),
        description: this.generateDescription(row, columnMap),
        impacts: {}
      };

      // Parse impact values
      IMPACT_CATEGORIES.forEach(category => {
        if (columnMap[category.id] !== undefined && row[columnMap[category.id]] !== undefined) {
          const value = parseFloat(row[columnMap[category.id]]);
          if (!isNaN(value)) {
            component.impacts[category.id] = value;
          }
        }
      });

      // Only add component if it has at least one impact value
      if (Object.keys(component.impacts).length > 0) {
        components.push(component);
      }
    });

    if (components.length === 0) {
      throw new Error('No valid component data found in Excel file. Please check the data format.');
    }

    return components;
  }

  determineCategory(row, columnMap) {
    if (columnMap.category !== undefined && row[columnMap.category]) {
      const category = row[columnMap.category].toString().trim();
      if (category) return category;
    }

    // Default category based on component name
    const name = row[columnMap.component_name].toString().toLowerCase();
    if (name.includes('foundation') || name.includes('structure') || name.includes('beam') || name.includes('column')) {
      return 'Structure';
    } else if (name.includes('wall') || name.includes('roof') || name.includes('window') || name.includes('door') || name.includes('insulation')) {
      return 'Envelope';
    } else if (name.includes('hvac') || name.includes('electrical') || name.includes('plumbing') || name.includes('mechanical')) {
      return 'MEP';
    } else if (name.includes('floor') || name.includes('ceiling') || name.includes('paint') || name.includes('finish')) {
      return 'Finishes';
    } else {
      return 'Other';
    }
  }

  generateDescription(row, columnMap) {
    const name = row[columnMap.component_name].toString();
    const category = this.determineCategory(row, columnMap);
    return `${category} component: ${name}`;
  }

  parseLCAStagesWorksheet(worksheet) {
    try {
      // Convert worksheet to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        console.warn('LCA Stages worksheet has insufficient data');
        return {};
      }

      const headers = jsonData[0];
      const rows = jsonData.slice(1);

      console.log('LCA Stages worksheet headers:', headers);
      console.log('LCA Stages worksheet rows:', rows.slice(0, 3)); // Show first 3 rows for debugging

      // Find the component column (first column)
      const componentColIndex = 0;
      
      // Find LCA stage columns (A1, A2, A3, A4, A5, B1, B2, B3, B4, B5, B6, B7, C1, C2, C3, C4, D)
      const stageColumns = [];
      for (let i = 1; i < headers.length; i++) {
        const header = headers[i]?.toString().trim();
        if (header && header !== '') {
          // Check if it's a valid LCA stage code (A1, A2, B1, B2, etc.)
          const stageCode = header.toString().trim();
          if (/^[A-Z]\d+$/.test(stageCode) || stageCode === 'D') {
            stageColumns.push({
              index: i,
              name: stageCode
            });
          }
        }
      }
      
      console.log('Found LCA stage columns:', stageColumns);

      if (stageColumns.length === 0) {
        console.warn('No LCA stage columns found in second worksheet. Expected format: A1, A2, B1, B2, etc.');
        return {};
      }

      // Debug: Show raw data for first few rows
      console.log('Raw LCA stages data (first 3 rows):');
      rows.slice(0, 3).forEach((row, idx) => {
        console.log(`  Row ${idx + 1}:`, row);
      });

      // Parse the data
      const lcaStagesData = {};
      
      rows.forEach((row, rowIndex) => {
        const componentName = row[componentColIndex]?.toString().trim();
        if (!componentName || componentName === '') return;

        console.log(`Processing row ${rowIndex + 1}: Component "${componentName}"`);

        // Try to match component name with components from first sheet
        const matchedComponentId = this.findMatchingComponentId(componentName);
        
        if (matchedComponentId) {
          lcaStagesData[matchedComponentId] = {
            component: componentName
          };

          console.log(`\nProcessing component: ${componentName} (ID: ${matchedComponentId})`);
          
          // Add stage values for each LCA stage column
          stageColumns.forEach(stageCol => {
            const rawValue = row[stageCol.index];
            const stageValue = parseFloat(rawValue);
            console.log(`  Raw value for ${stageCol.name}: "${rawValue}" (type: ${typeof rawValue})`);
            
            if (!isNaN(stageValue)) { // Accept all valid numbers, including negative values
              lcaStagesData[matchedComponentId][stageCol.name] = stageValue;
              console.log(`  ✓ Parsed ${stageCol.name}: ${stageValue} ${stageValue < 0 ? '(NEGATIVE)' : ''}`);
            } else {
              console.log(`  ✗ Invalid value for ${stageCol.name}: "${rawValue}"`);
            }
          });
        } else {
          console.warn(`Could not match component "${componentName}" with any component from first sheet`);
        }
      });

      console.log('Final parsed LCA stages data:', lcaStagesData);
      return lcaStagesData;
    } catch (error) {
      console.error('Error parsing LCA stages worksheet:', error);
      return {};
    }
  }

  findMatchingComponentId(componentName) {
    // This method will be called after components are parsed
    const normalizedName = componentName.toLowerCase().trim();
    
    // Look through the parsed components to find a match
    if (this.parsedComponents) {
      for (const component of this.parsedComponents) {
        const componentNormalizedName = component.name.toLowerCase().trim();
        
        // Exact match (most reliable)
        if (componentNormalizedName === normalizedName) {
          console.log(`Exact match found: "${componentName}" -> "${component.name}" (ID: ${component.id})`);
          return component.id;
        }
        
        // Case-insensitive exact match
        if (componentNormalizedName === normalizedName) {
          console.log(`Case-insensitive match found: "${componentName}" -> "${component.name}" (ID: ${component.id})`);
          return component.id;
        }
        
        // Partial match (component name contains the stage name or vice versa)
        if (componentNormalizedName.includes(normalizedName) || 
            normalizedName.includes(componentNormalizedName)) {
          console.log(`Partial match found: "${componentName}" -> "${component.name}" (ID: ${component.id})`);
          return component.id;
        }
      }
    }
    
    // If no match found, log it and create a new ID
    console.warn(`No match found for component "${componentName}". Creating new ID.`);
    return `component_${componentName.replace(/[^a-zA-Z0-9]/g, '_')}`;
  }

  transformToLCAData(parsedData, lcaStagesData = {}) {
    const { buildingInfo, components } = parsedData;
    
    // Store components for matching in LCA stages
    this.parsedComponents = components;
    
    // Ensure buildingInfo is always valid
    const validBuildingInfo = buildingInfo || {
      name: 'LCA Project',
      area: 1000,
      location: 'Unknown',
      serviceLife: 50
    };
    
    // Validate buildingInfo properties
    if (!validBuildingInfo.name || validBuildingInfo.name === '') validBuildingInfo.name = 'LCA Project';
    if (!validBuildingInfo.area || isNaN(validBuildingInfo.area)) validBuildingInfo.area = 1000;
    if (!validBuildingInfo.location || validBuildingInfo.location === '') validBuildingInfo.location = 'Unknown';
    if (!validBuildingInfo.serviceLife || isNaN(validBuildingInfo.serviceLife)) validBuildingInfo.serviceLife = 50;
    
    console.log('Final building info for LCA data:', validBuildingInfo);
    
    // Calculate total impacts
    const totalImpacts = {};
    IMPACT_CATEGORIES.forEach(category => {
      totalImpacts[category.id] = components.reduce((sum, component) => {
        return sum + (component.impacts[category.id] || 0);
      }, 0);
    });

    return {
      buildingInfo: validBuildingInfo,
      impactCategories: IMPACT_CATEGORIES,
      components,
      totalImpacts,
      lcaStagesData, // Include LCA stages data from second sheet
      projectId: `project_${Date.now()}`,
      projectName: validBuildingInfo.name,
      description: `LCA analysis for ${validBuildingInfo.name}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
}

// Helper functions for data transformation
export const transformLCAData = (parsedData) => {
  return parsedData;
};

export const getImpactCategoryData = (lcaData, categoryId) => {
  const category = lcaData.impactCategories.find(cat => cat.id === categoryId);
  if (!category) return null;

  const total = lcaData.totalImpacts[categoryId] || 0;
  const data = lcaData.components.map(component => {
    const value = component.impacts[categoryId] || 0;
    return {
      name: component.name,
      category: component.category,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0
    };
  });

  return {
    category,
    data,
    total
  };
};

export const getComponentsByCategory = (components) => {
  const categories = {};
  components.forEach(component => {
    if (!categories[component.category]) {
      categories[component.category] = [];
    }
    categories[component.category].push(component);
  });
  return categories;
};

export default ExcelParser;
